import { useCallback, useEffect, useState } from "react";
import { api } from "./api";
import { Game } from "./screens/Game";
import { Results } from "./screens/Results";
import { Signup } from "./screens/Signup";
import { Start } from "./screens/Start";
import { useSettings } from "./settings";
import { cohortCodeFromUrl, urlWithoutCode } from "./signup";
import type { User } from "./types";

type Screen =
  | { kind: "loading" }
  | { kind: "signup" }
  | { kind: "start"; user: User }
  | { kind: "game"; user: User; runId: string };

/** Keeps the open run in the URL hash so a refresh returns to the same office. */
function runFromHash(): string | null {
  const m = /^#\/run\/([A-Za-z0-9_-]+)$/.exec(window.location.hash);
  return m ? m[1] ?? null : null;
}

const isResults = () => window.location.hash === "#/results";

const DEFAULT_ORG = "Operate Legal";

export function App() {
  // The researcher results page (#/results) stands apart from the game: no player sign-in, admin token only.
  const [results, setResults] = useState(isResults);
  useEffect(() => {
    const onHash = () => setResults(isResults());
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);
  return results ? <Results /> : <Player />;
}

function Player() {
  const [screen, setScreen] = useState<Screen>({ kind: "loading" });
  const [settings, setSettings] = useSettings();
  const [orgName, setOrgName] = useState(DEFAULT_ORG);
  // A cohort link (?code=MP-OCT, or #/?code=MP-OCT) tags the sign-up; it is never required.
  const [code] = useState(() => cohortCodeFromUrl(window.location.search, window.location.hash));

  useEffect(() => {
    api.config().then((c) => setOrgName(c.orgName || DEFAULT_ORG)).catch(() => undefined);
    api.me()
      .then(({ user }) => {
        const runId = runFromHash();
        setScreen(runId ? { kind: "game", user, runId } : { kind: "start", user });
      })
      .catch(() => setScreen({ kind: "signup" }));
  }, []);

  const signedUp = (user: User) => {
    // The code has done its job; keep it out of bookmarks and the game's own addresses.
    if (code) history.replaceState(null, "", urlWithoutCode(window.location.pathname, window.location.search, window.location.hash));
    setScreen({ kind: "start", user });
  };

  const openRun = (user: User, runId: string) => {
    window.location.hash = `/run/${runId}`;
    setScreen({ kind: "game", user, runId });
  };
  const toStart = (user: User) => {
    history.replaceState(null, "", window.location.pathname);
    setScreen({ kind: "start", user });
  };
  const signedOut = useCallback(() => setScreen({ kind: "signup" }), []);

  switch (screen.kind) {
    case "loading":
      return <main className="entry"><p aria-live="polite">Loading…</p></main>;
    case "signup":
      return <Signup orgName={orgName} code={code} onSignedUp={signedUp} />;
    case "start":
      return (
        <Start user={screen.user} onOpenRun={(id) => openRun(screen.user, id)}
          onLogout={() => { void api.logout().finally(() => setScreen({ kind: "signup" })); }} />
      );
    case "game":
      return (
        <Game key={screen.runId} runId={screen.runId} settings={settings} onSettings={setSettings} orgName={orgName}
          onExit={() => toStart(screen.user)} onSignedOut={signedOut} />
      );
  }
}
